import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Globe, TrendingUp, Shield, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import PropertyCard from '../components/PropertyCard';
import Newsletter from '../components/Newsletter';
import { NextConsultationStrip } from '../components/NextConsultationStrip';
import { useProjects, REGION_KEY_BY_LABEL } from '../hooks/useProjects';
import { useTranslation } from 'react-i18next';

const Home = () => {
  const { t, i18n } = useTranslation();
  const { properties } = useProjects();
  const featuredProperties = properties.filter(p => p.featured);
  const activeRegionKeys = [...new Set(properties.map(p => REGION_KEY_BY_LABEL[p.location]).filter(Boolean))];

  useEffect(() => {
    document.title = 'Fundora Global - Trusted Global Real Estate Investments';
  }, []);

  // Get translated region data
  const getTranslatedRegions = () => [
    { key: 'northernCyprus', name: t('regions.northernCyprus.name'), description: t('regions.northernCyprus.description'), count: t('regions.northernCyprus.count') },
    { key: 'bali', name: t('regions.bali.name'), description: t('regions.bali.description'), count: t('regions.bali.count') },
    { key: 'vietnam', name: t('regions.vietnam.name'), description: t('regions.vietnam.description'), count: t('regions.vietnam.count') },
    { key: 'maldives', name: t('regions.maldives.name'), description: t('regions.maldives.description'), count: t('regions.maldives.count') }
  ];

  // Map region keys to URL-compatible names (English)
  const regionUrlMap = {
    northernCyprus: 'Northern Cyprus',
    bali: 'Bali (Indonesia)',
    vietnam: 'Vietnam',
    maldives: 'Maldives'
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Background Image with Opacity */}
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-70"
          style={{
            backgroundImage: "url('https://customer-assets.emergentagent.com/job_luxe-estates-6/artifacts/bwgjs5g8_image_2025-12-23_102838747.png')"
          }}
        ></div>
        
        {/* Background Color Overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#102131]/70 via-[#1a3449]/70 to-[#102131]/70">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-20 left-20 w-96 h-96 bg-[#CDA259] rounded-full blur-3xl"></div>
            <div className="absolute bottom-20 right-20 w-96 h-96 bg-[#CDA259] rounded-full blur-3xl"></div>
          </div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-7xl font-bold text-[#F7FDFF] mb-6 animate-fade-in">
            {t('home.heroTitle1')}
            <span className="block mt-2 bg-gradient-to-r from-[#CDA259] to-[#e6b96f] bg-clip-text text-transparent">
              {t('home.heroTitle2')}
            </span>
          </h1>
          <p className="text-xl md:text-2xl text-[#F7FDFF]/80 mb-12 max-w-3xl mx-auto">
            {t('home.heroSubtitle')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/properties">
              <Button size="lg" className="bg-[#CDA259] text-[#102131] hover:bg-[#CDA259]/90 font-semibold px-8 py-6 text-lg transition-all duration-200 hover:scale-105">
                {t('home.exploreCases')}
                <ArrowRight className="ml-2" size={20} />
              </Button>
            </Link>
            <Link to="/contact">
              <Button size="lg" variant="outline" className="border-[#CDA259] text-[#F7FDFF] hover:bg-[#CDA259]/10 font-semibold px-8 py-6 text-lg transition-all duration-200">
                {t('nav.bookConsultation')}
              </Button>
            </Link>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <ChevronRight size={32} className="text-[#CDA259] rotate-90" />
        </div>
      </section>

      <NextConsultationStrip />

      {/* Why Fundora Section */}
      <section className="py-20 bg-[#F7FDFF]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#102131] mb-4">
              {t('home.whyChoose')}
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              {t('home.whyChooseSubtitle')}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="group p-8 bg-white rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 border border-[#CDA259]/10">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-[#CDA259]/10 rounded-full mb-6 group-hover:bg-[#CDA259]/20 transition-colors">
                <Shield size={32} className="text-[#CDA259]" />
              </div>
              <h3 className="text-2xl font-bold text-[#102131] mb-4">{t('home.trustExperience')}</h3>
              <p className="text-gray-600">
                {t('home.trustExperienceDesc')}
              </p>
            </div>

            <div className="group p-8 bg-white rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 border border-[#CDA259]/10">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-[#CDA259]/10 rounded-full mb-6 group-hover:bg-[#CDA259]/20 transition-colors">
                <Globe size={32} className="text-[#CDA259]" />
              </div>
              <h3 className="text-2xl font-bold text-[#102131] mb-4">{t('home.globalReach')}</h3>
              <p className="text-gray-600">
                {t('home.globalReachDesc')}
              </p>
            </div>

            <div className="group p-8 bg-white rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 border border-[#CDA259]/10">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-[#CDA259]/10 rounded-full mb-6 group-hover:bg-[#CDA259]/20 transition-colors">
                <TrendingUp size={32} className="text-[#CDA259]" />
              </div>
              <h3 className="text-2xl font-bold text-[#102131] mb-4">{t('home.sustainableGrowth')}</h3>
              <p className="text-gray-600">
                {t('home.sustainableGrowthDesc')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Properties */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-12">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold text-[#102131] mb-4">
                {t('home.featuredCases')}
              </h2>
              <p className="text-lg text-gray-600">
                {t('home.featuredCasesSubtitle')}
              </p>
            </div>
            <Link to="/properties">
              <Button variant="outline" className="border-[#CDA259] text-[#102131] hover:bg-[#CDA259]/10 hidden md:flex">
                {t('home.viewAll')}
                <ArrowRight className="ml-2" size={18} />
              </Button>
            </Link>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {featuredProperties.map((property) => (
              <PropertyCard key={property.id} property={property} />
            ))}
          </div>

          <div className="text-center mt-12 md:hidden">
            <Link to="/properties">
              <Button className="bg-[#CDA259] text-[#102131] hover:bg-[#CDA259]/90">
                {t('home.viewAllCases')}
                <ArrowRight className="ml-2" size={18} />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Regions Section */}
      <section className="py-20 bg-[#F7FDFF]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#102131] mb-4">
              {t('home.investmentRegions')}
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              {t('home.investmentRegionsSubtitle')}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {getTranslatedRegions().filter((region) => activeRegionKeys.includes(region.key)).map((region) => (
              <Link
                key={region.key}
                to={`/properties?region=${encodeURIComponent(regionUrlMap[region.key])}`}
                className="group p-6 bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border border-[#CDA259]/10 cursor-pointer"
              >
                <h3 className="text-2xl font-bold text-[#102131] mb-2 group-hover:text-[#CDA259] transition-colors">
                  {region.name}
                </h3>
                <p className="text-gray-600 mb-3">{region.description}</p>
                <p className="text-sm font-semibold text-[#CDA259]">{region.count}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-[#102131] to-[#1a3449] text-[#F7FDFF] relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-0 left-0 w-96 h-96 bg-[#CDA259] rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#CDA259] rounded-full blur-3xl"></div>
        </div>
        
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            {t('home.readyToInvest')}
          </h2>
          <p className="text-xl text-[#F7FDFF]/80 mb-8">
            {t('home.readyToInvestDesc')}
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-[#CDA259] text-[#102131] hover:bg-[#CDA259]/90 font-semibold px-8 py-6 text-lg transition-all duration-200 hover:scale-105">
              {t('home.bookAConsultation')}
              <ArrowRight className="ml-2" size={20} />
            </Button>
          </Link>
        </div>
      </section>

      {/* Newsletter */}
      <Newsletter />
    </div>
  );
};

export default Home;
